import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Modal from "../../../Ui/Modal";
import ButtonComponent from "../../../Ui/Button";

// Modal to accept or deny a reschedule request (stubbed actions)
// Props:
// - open: boolean
// - onClose: function
// - reschedule: object|null
// - onAccept: function (chosenOption)
// - onDeny: function (reason)
const RescheduleRequestAcceptDeny = ({ open, onClose, reschedule, onAccept, onDeny }) => {
  // Normalize a field value from multiple possible keys
  const getField = (obj, keys = []) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && String(v).length > 0) return v;
    }
    return null;
  };

  const reason = getField(reschedule || {}, ["reason", "message", "note"]);

  // Reschedule availability options (as provided by the requester)
  // Expect an array like ["2025-01-10|morning", "2025-01-11|allDay", ...]
  const rawDays = useMemo(() => {
    let d =
      reschedule?.days_available ||
      reschedule?.daysAvailable ||
      reschedule?.available_days ||
      reschedule?.availability ||
      [];
    if (typeof d === "string") {
      try {
        const parsed = JSON.parse(d);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // keep as-is
      }
      d = [];
    }
    return Array.isArray(d) ? d : [];
  }, [reschedule]);

  // Human labels for the time slots – keep consistent with elsewhere in app
  const SLOT_RANGE_LABELS = {
    morning: "Morning (8am - 12pm)",
    afternoon: "Afternoon (12pm - 3pm)",
    evening: "Evening (3pm - 5pm)",
    allDay: "All Day (8am - 5pm)",
    allday: "All Day (8am - 5pm)", // tolerate casing/variants
  };

  // Group availability by date
  const groupedAvailability = useMemo(() => {
    const grouped = {};
    (rawDays || []).forEach((entry) => {
      if (typeof entry !== "string") return;
      if (!entry.includes("|")) return;
      const [iso, slotRaw] = entry.split("|");
      const slotKey = String(slotRaw || "");
      const label = SLOT_RANGE_LABELS[slotKey] || SLOT_RANGE_LABELS[slotKey.toLowerCase()] || slotKey;
      if (!grouped[iso]) grouped[iso] = [];
      if (!grouped[iso].includes(label)) grouped[iso].push(label);
    });
    return Object.keys(grouped)
      .sort()
      .map((iso) => ({
        dateLabel: dayjs(iso).isValid() ? dayjs(iso).format("ddd, MMM D") : iso,
        slots: grouped[iso],
      }));
  }, [rawDays]);

  // Flat option list for selection controls
  const optionList = useMemo(() => {
    const items = [];
    (rawDays || []).forEach((entry) => {
      if (typeof entry !== "string" || !entry.includes("|")) return;
      const [iso, slotRaw] = entry.split("|");
      const slotKey = String(slotRaw || "");
      const labelSlot = SLOT_RANGE_LABELS[slotKey] || SLOT_RANGE_LABELS[slotKey.toLowerCase()] || slotKey;
      const dateLabel = dayjs(iso).isValid() ? dayjs(iso).format("ddd, MMM D") : iso;
      items.push({ value: `${iso}|${slotRaw}`, label: `${dateLabel} — ${labelSlot}` });
    });
    return items;
  }, [rawDays]);

  const [selectedOption, setSelectedOption] = useState("");
  const [denyReason, setDenyReason] = useState("");

  // Preselect only option if exactly one provided
  useEffect(() => {
    if (!open) return;
    if (optionList.length === 1) {
      setSelectedOption(optionList[0].value);
    } else {
      setSelectedOption("");
    }
    setDenyReason("");
  }, [open, optionList]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="756px"
      title="Respond to Reschedule Request"
      footer={
        <div className="flex w-full justify-between">
          <ButtonComponent paddingY="10px" onClick={onClose}>
            Close
          </ButtonComponent>
          <div className="flex gap-2">
            <ButtonComponent
              bgColor="#D40000"
              color="#FFFFFF"
              paddingY="10px"
              onClick={() => onDeny && onDeny(denyReason)}
            >
              Deny Request
            </ButtonComponent>
            <ButtonComponent
              bgColor="#3CAB5C"
              color="#FFFFFF"
              paddingY="10px"
              onClick={() => onAccept && onAccept(selectedOption || null)}
              disabled={optionList.length > 1 && !selectedOption}
            >
              Accept Request
            </ButtonComponent>
          </div>
        </div>
      }
    >
      <div className="px-4 py-4 space-y-4">
        <h3 className="text-[16px] font-semibold text-[#242424]">Reschedule Availability</h3>

        {/* Availability options list */}
        {groupedAvailability.length > 0 ? (
          <div className="space-y-3">
            {groupedAvailability.map(({ dateLabel, slots }, idx) => (
              <div key={`${dateLabel}-${idx}`} className="flex items-start justify-between gap-4">
                <span className="text-[#242424] text-[14px] font-medium min-w-[140px]">{dateLabel}</span>
                <div className="text-[#595959] text-[14px] break-words">
                  {slots.join(", ")}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#595959] text-[14px]">No availability options were provided.</p>
        )}

        {/* Select field when multiple options are available */}
        {optionList.length > 0 && (
          <div className="pt-2">
            <div className="text-[14px] text-[#242424] font-medium mb-2">Choose one option to approve</div>
            <div className="flex flex-col gap-2 max-h-48 overflow-auto pr-1">
              {optionList.map((opt) => (
                <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reschedule_option"
                    value={opt.value}
                    checked={selectedOption === opt.value}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    className="mt-1"
                  />
                  <span className="text-[14px] text-[#595959]">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Reason (optional) */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-4">
            <span className="text-[#242424] text-[14px] font-medium min-w-[140px]">Reason from requester</span>
            <span className="text-[#595959] text-[14px] break-words whitespace-pre-wrap">{reason || "No reason provided"}</span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[14px] text-[#242424] font-medium">Optional message (for denying)</label>
            <textarea
              rows={3}
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="Add a short explanation if you choose to deny"
              className="w-full border border-[#ECECEC] rounded-md p-2 text-[14px] text-[#242424] outline-none focus:ring-2 focus:ring-[#3CAB5C]"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RescheduleRequestAcceptDeny;
